/// <amd-module name="VSS/Platform/String" />

import { IDateTimeFormatSettings, ICultureInfo, getCurrentCulture, getInvariantCulture, checkIfCustomerCurrentCultureExists } from "./Culture";

export function dateToString(value: Date, useLocale?:boolean, format?: string): string {
    return MicrosoftAjaxDateFormatting.dateToString(value, useLocale, format || "g");
}

export function parseShortTimeString(value: string) {
    if (checkIfCustomerCurrentCultureExists()) {
        return MicrosoftAjaxDateFormatting.parseShortTimeString(value, getCurrentCulture());
    }

    return undefined
}

/**
 * Generate a string using a format string and arguments.
 *
 * @param format Format string
 * @param args Arguments to use as replacements
 */
export function format(format: string, ...args: any[]): string {
    return _stringFormat(false, format, args);
}

namespace MicrosoftAjaxDateFormatting {

    function _getDateEra(date: Date, eras: any[]): number {
        if (!eras) {
            return 0;
        }
        let start;
        const ticks = date.getTime();
        for (let i = 0, l = eras.length; i < l; i += 4) {
            start = eras[i + 2];
            if (start === null || ticks >= start) {
                return i;
            }
        }
        return 0;
    }

    function _getDateEraYear(date: Date, dtf: IDateTimeFormatSettings, era: number, sortable?: boolean) {
        let year = date.getFullYear();

        // If we have a locale conversion function then we need to use it to get the year before applying the era
        const convert = dtf.Calendar.convert;
        if (convert) {
            year = convert.fromGregorian(date)[0];
        }

        if (!sortable && dtf.eras) {
            year -= dtf.eras[era + 3];
        }
        return year;
    }

    function _expandDateFormat(dtf: IDateTimeFormatSettings, format: string) {
        if (!format) {
            format = "F";
        }
        const len = format.length;
        if (len === 1) {
            switch (format) {
                case "d":
                    return dtf.ShortDatePattern;
                case "D":
                    return dtf.LongDatePattern;
                case "g":
                    return dtf.ShortDatePattern + " " + dtf.ShortTimePattern;
                case "G":
                    return dtf.ShortDatePattern + " " + dtf.LongTimePattern;
                case "t":
                    return dtf.ShortTimePattern;
                case "T":
                    return dtf.LongTimePattern;
                case "f":
                    return dtf.LongDatePattern + " " + dtf.ShortTimePattern;
                case "F":
                    return dtf.FullDateTimePattern;
                case "M":
                    return dtf.MonthDayPattern;
                case "m":
                    return getShortDayMonthPatten(dtf);
                case "s":
                    return dtf.SortableDateTimePattern;
                case "Y":
                case "y":
                    return dtf.YearMonthPattern;
                case "R":
                case "r":
                    return dtf.RFC1123Pattern;
                case "u":
                    return dtf.UniversalSortableDateTimePattern;
                default:
                    throw new Error("Input string was not in a correct format.");
            }
        } else if (len === 2 && format.charAt(0) === "%") {
            format = format.charAt(1);
        }
        return format;
    }

    export function dateToString(value: Date, useLocale?: boolean, format?: string): string {
        const dtf = useLocale ? getCurrentCulture().dateTimeFormat : getInvariantCulture().dateTimeFormat;
        const convert = dtf.Calendar.convert;

        if (!format || !format.length || format === "i") {
            if (useLocale) {
                if (convert) {
                    format = dtf.FullDateTimePattern;
                } else {
                    const eraDate = new Date(value.getTime());
                    const era = _getDateEra(value, dtf.eras);
                    eraDate.setFullYear(_getDateEraYear(value, dtf, era));
                    return eraDate.toLocaleString();
                }
            } else {
                return value.toString();
            }
        }
        const eras = dtf.eras;
        const sortable = format === "s";
        format = _expandDateFormat(dtf, format);
        const ret: string[] = [];
        let hour;
        function addLeadingZero(num: number) {
            if (num < 10) {
                return "0" + num;
            }
            return num.toString();
        }
        function addLeadingZeros(num: number) {
            if (num < 10) {
                return "00" + num;
            }
            if (num < 100) {
                return "0" + num;
            }
            return num.toString();
        }
        function padYear(year: number) {
            if (year < 10) {
                return "000" + year;
            } else if (year < 100) {
                return "00" + year;
            } else if (year < 1000) {
                return "0" + year;
            }
            return year.toString();
        }
        function _appendPreOrPostMatch(preMatch: string, result: string[]) {
            let quoteCount = 0,
                escaped = false;
            for (let i = 0, il = preMatch.length; i < il; i++) {
                const c = preMatch.charAt(i);
                switch (c) {
                    case "'":
                        if (escaped) {
                            result.push("'");
                        } else {
                            quoteCount++;
                        }
                        escaped = false;
                        break;
                    case "\\":
                        if (escaped) {
                            result.push("\\");
                        }
                        escaped = !escaped;
                        break;
                    default:
                        result.push(c);
                        escaped = false;
                        break;
                }
            }
            return quoteCount;
        }

        let foundDay: boolean, checkedDay: boolean;
        const dayPartRegExp = /([^d]|^)(d|dd)([^d]|$)/g;
        function hasDay() {
            if (foundDay || checkedDay) {
                return foundDay;
            }
            foundDay = format ? dayPartRegExp.test(format) : false;
            checkedDay = true;
            return foundDay;
        }

        let quoteCount = 0,
            converted: number[] = [];
        const tokenRegExp = /\/|dddd|ddd|dd|d|MMMM|MMM|MM|M|yyyy|yy|y|hh|h|HH|H|mm|m|ss|s|tt|t|fff|ff|f|zzz|zz|z|gg|g/g;

        if (!sortable && convert) {
            converted = convert.fromGregorian(value);
        }
        function getPart(date: any, part: number) {
            if (converted.length !== 0) {
                return converted[part];
            }
            switch (part) {
                case 0:
                    return date.getFullYear();
                case 1:
                    return date.getMonth();
                case 2:
                    return date.getDate();
            }
        }
        for (;;) {
            const index = tokenRegExp.lastIndex;
            const ar = tokenRegExp.exec(format);
            const preMatch = format.slice(index, ar ? ar.index : format.length);
            quoteCount += _appendPreOrPostMatch(preMatch, ret);
            if (!ar) {
                break;
            }
            if (quoteCount % 2 === 1) {
                ret.push(ar[0]);
                continue;
            }

            switch (ar[0]) {
                case "dddd":
                    ret.push(dtf.DayNames[value.getDay()]);
                    break;
                case "ddd":
                    ret.push(dtf.AbbreviatedDayNames[value.getDay()]);
                    break;
                case "dd":
                    foundDay = true;
                    ret.push(addLeadingZero(getPart(value, 2)));
                    break;
                case "d":
                    foundDay = true;
                    ret.push(getPart(value, 2));
                    break;
                case "MMMM":
                    ret.push(
                        dtf.MonthGenitiveNames && dtf.MonthGenitiveNames.length !== 0 && hasDay()
                            ? dtf.MonthGenitiveNames[getPart(value, 1)]
                            : dtf.MonthNames[getPart(value, 1)]
                    );
                    break;
                case "MMM":
                    ret.push(
                        dtf.AbbreviatedMonthGenitiveNames && dtf.AbbreviatedMonthGenitiveNames.length !== 0 && hasDay()
                            ? dtf.AbbreviatedMonthGenitiveNames[getPart(value, 1)]
                            : dtf.AbbreviatedMonthNames[getPart(value, 1)]
                    );
                    break;
                case "MM":
                    ret.push(addLeadingZero(getPart(value, 1) + 1));
                    break;
                case "M":
                    ret.push(getPart(value, 1) + 1);
                    break;
                case "yyyy":
                    ret.push(padYear(converted.length !== 0 ? converted[0] : _getDateEraYear(value, dtf, _getDateEra(value, eras), sortable)));
                    break;
                case "yy":
                    ret.push(
                        addLeadingZero(
                            (converted.length !== 0 ? converted[0] : _getDateEraYear(value, dtf, _getDateEra(value, eras), sortable)) % 100
                        )
                    );
                    break;
                case "y":
                    ret.push(
                        ((converted.length !== 0 ? converted[0] : _getDateEraYear(value, dtf, _getDateEra(value, eras), sortable)) % 100).toString()
                    );
                    break;
                case "hh":
                    hour = value.getHours() % 12;
                    if (hour === 0) {
                        hour = 12;
                    }
                    ret.push(addLeadingZero(hour));
                    break;
                case "h":
                    hour = value.getHours() % 12;
                    if (hour === 0) {
                        hour = 12;
                    }
                    ret.push(hour.toString());
                    break;
                case "HH":
                    ret.push(addLeadingZero(value.getHours()));
                    break;
                case "H":
                    ret.push(value.getHours().toString());
                    break;
                case "mm":
                    ret.push(addLeadingZero(value.getMinutes()));
                    break;
                case "m":
                    ret.push(value.getMinutes().toString());
                    break;
                case "ss":
                    ret.push(addLeadingZero(value.getSeconds()));
                    break;
                case "s":
                    ret.push(value.getSeconds().toString());
                    break;
                case "tt":
                    ret.push(value.getHours() < 12 ? dtf.AMDesignator : dtf.PMDesignator);
                    break;
                case "t":
                    ret.push((value.getHours() < 12 ? dtf.AMDesignator : dtf.PMDesignator).charAt(0));
                    break;
                case "f":
                    ret.push(addLeadingZeros(value.getMilliseconds()).charAt(0));
                    break;
                case "ff":
                    ret.push(addLeadingZeros(value.getMilliseconds()).substr(0, 2));
                    break;
                case "fff":
                    ret.push(addLeadingZeros(value.getMilliseconds()));
                    break;
                case "z":
                    hour = value.getTimezoneOffset() / 60;
                    ret.push((hour <= 0 ? "+" : "-") + Math.floor(Math.abs(hour)));
                    break;
                case "zz":
                    hour = value.getTimezoneOffset() / 60;
                    ret.push((hour <= 0 ? "+" : "-") + addLeadingZero(Math.floor(Math.abs(hour))));
                    break;
                case "zzz":
                    hour = value.getTimezoneOffset() / 60;
                    ret.push(
                        (hour <= 0 ? "+" : "-") +
                            addLeadingZero(Math.floor(Math.abs(hour))) +
                            ":" +
                            addLeadingZero(Math.abs(value.getTimezoneOffset() % 60))
                    );
                    break;
                case "g":
                case "gg":
                    if (dtf.eras) {
                        ret.push(dtf.eras[_getDateEra(value, eras) + 1]);
                    }
                    break;
                case "/":
                    ret.push(dtf.DateSeparator);
                    break;
                default:
                    new Error("Invalid date format pattern");
                    break;
            }
        }
        return ret.join("");
    }

    export function parseShortTimeString(value: string, cultureInfo: ICultureInfo): Date | undefined {
        const shortTimeFormat = cultureInfo.dateTimeFormat.ShortTimePattern;
        return _parseExact(value, shortTimeFormat, cultureInfo) ?? undefined;
    }

    function _parseExact(value: string, format: string, cultureInfo: ICultureInfo) {
        value = value.trim();
        const dtf = cultureInfo.dateTimeFormat;
        const parseInfo = _getParseRegExp(dtf, format);
        const match = new RegExp(parseInfo.regExp).exec(value);
        if (match === null) {
            return null;
        }

        const groups = parseInfo.groups;
        let era = null,
            year = null,
            month = null,
            date = null,
            weekDay = null,
            hour = 0,
            hourOffset,
            min = 0,
            sec = 0,
            msec = 0,
            tzMinOffset = null,
            pmHour = false;
        for (let j = 0, jl = groups.length; j < jl; j++) {
            const matchGroup = match[j + 1];
            if (matchGroup) {
                switch (groups[j]) {
                    case "dd":
                    case "d":
                        date = parseInt(matchGroup, 10);
                        if (date < 1 || date > 31) {
                            return null;
                        }
                        break;
                    case "MMMM":
                        month = _getMonthIndex(cultureInfo, matchGroup);
                        if (month < 0 || month > 11) {
                            return null;
                        }
                        break;
                    case "MMM":
                        month = _getAbbrMonthIndex(cultureInfo, matchGroup);
                        if (month < 0 || month > 11) {
                            return null;
                        }
                        break;
                    case "M":
                    case "MM":
                        month = parseInt(matchGroup, 10) - 1;
                        if (month < 0 || month > 11) {
                            return null;
                        }
                        break;
                    case "y":
                    case "yy":
                        year = _expandYear(dtf, parseInt(matchGroup, 10));
                        if (year < 0 || year > 9999) {
                            return null;
                        }
                        break;
                    case "yyyy":
                        year = parseInt(matchGroup, 10);
                        if (year < 0 || year > 9999) {
                            return null;
                        }
                        break;
                    case "h":
                    case "hh":
                        hour = parseInt(matchGroup, 10);
                        if (hour === 12) {
                            hour = 0;
                        }
                        if (hour < 0 || hour > 11) {
                            return null;
                        }
                        break;
                    case "H":
                    case "HH":
                        hour = parseInt(matchGroup, 10);
                        if (hour < 0 || hour > 23) {
                            return null;
                        }
                        break;
                    case "m":
                    case "mm":
                        min = parseInt(matchGroup, 10);
                        if (min < 0 || min > 59) {
                            return null;
                        }
                        break;
                    case "s":
                    case "ss":
                        sec = parseInt(matchGroup, 10);
                        if (sec < 0 || sec > 59) {
                            return null;
                        }
                        break;
                    case "tt":
                    case "t":
                        const upperToken = matchGroup.toUpperCase();
                        pmHour = upperToken === dtf.PMDesignator.toUpperCase();
                        if (!pmHour && upperToken !== dtf.AMDesignator.toUpperCase()) {
                            return null;
                        }
                        break;
                    case "f":
                        msec = parseInt(matchGroup, 10) * 100;
                        if (msec < 0 || msec > 999) {
                            return null;
                        }
                        break;
                    case "ff":
                        msec = parseInt(matchGroup, 10) * 10;
                        if (msec < 0 || msec > 999) {
                            return null;
                        }
                        break;
                    case "fff":
                        msec = parseInt(matchGroup, 10);
                        if (msec < 0 || msec > 999) {
                            return null;
                        }
                        break;
                    case "dddd":
                        weekDay = _getDayIndex(cultureInfo, matchGroup);
                        if (weekDay < 0 || weekDay > 6) {
                            return null;
                        }
                        break;
                    case "ddd":
                        weekDay = _getAbbrDayIndex(cultureInfo, matchGroup);
                        if (weekDay < 0 || weekDay > 6) {
                            return null;
                        }
                        break;
                    case "zzz":
                        const offsets = matchGroup.split(/:/);
                        if (offsets.length !== 2) {
                            return null;
                        }
                        hourOffset = parseInt(offsets[0], 10);
                        if (hourOffset < -12 || hourOffset > 13) {
                            return null;
                        }
                        const minOffset = parseInt(offsets[1], 10);
                        if (minOffset < 0 || minOffset > 59) {
                            return null;
                        }
                        tzMinOffset = hourOffset * 60 + (matchGroup.startsWith("-") ? -minOffset : minOffset);
                        break;
                    case "z":
                    case "zz":
                        hourOffset = parseInt(matchGroup, 10);
                        if (hourOffset < -12 || hourOffset > 13) {
                            return null;
                        }
                        tzMinOffset = hourOffset * 60;
                        break;
                    case "g":
                    case "gg":
                        let eraName = matchGroup;
                        if (!eraName || !dtf.eras) {
                            return null;
                        }
                        eraName = eraName.toLowerCase().trim();
                        for (let i = 0, l = dtf.eras.length; i < l; i += 4) {
                            if (eraName === dtf.eras[i + 1].toLowerCase()) {
                                era = i;
                                break;
                            }
                        }
                        if (era === null) {
                            return null;
                        }
                        break;
                }
            }
        }
        let result = new Date(),
            defaultYear;
        const convert = dtf.Calendar.convert;
        if (convert) {
            defaultYear = convert.fromGregorian(result)[0];
        } else {
            defaultYear = result.getFullYear();
        }
        if (year === null) {
            year = defaultYear;
        } else if (dtf.eras) {
            year += dtf.eras[(era || 0) + 3];
        }
        if (month === null) {
            month = 0;
        }
        if (date === null) {
            date = 1;
        }
        if (convert) {
            result = convert.toGregorian(year, month, date);
            if (result === null) {
                return null;
            }
        } else {
            result.setFullYear(year, month, date);
            if (result.getDate() !== date) {
                return null;
            }
            if (weekDay !== null && result.getDay() !== weekDay) {
                return null;
            }
        }
        if (pmHour && hour < 12) {
            hour += 12;
        }
        result.setHours(hour, min, sec, msec);
        if (tzMinOffset !== null) {
            const adjustedMin = result.getMinutes() - (tzMinOffset + result.getTimezoneOffset());
            result.setHours(result.getHours() + parseInt((adjustedMin / 60) as any, 10), adjustedMin % 60);
        }
        return result;
    }

    function _appendPreOrPostMatch(preMatch: string, result: string[]) {
        let quoteCount = 0,
            escaped = false;
        for (let i = 0, il = preMatch.length; i < il; i++) {
            const c = preMatch.charAt(i);
            switch (c) {
                case "'":
                    if (escaped) {
                        result.push("'");
                    } else {
                        quoteCount++;
                    }
                    escaped = false;
                    break;
                case "\\":
                    if (escaped) {
                        result.push("\\");
                    }
                    escaped = !escaped;
                    break;
                default:
                    result.push(c);
                    escaped = false;
                    break;
            }
        }
        return quoteCount;
    }

    function _expandYear(dtf: IDateTimeFormatSettings, year: number) {
        const now = new Date(),
            era = _getDateEra(now, dtf.eras);

        if (year < 100) {
            const curr = _getDateEraYear(now, dtf, era);
            year += curr - (curr % 100);
            if (year > dtf.Calendar.TwoDigitYearMax) {
                year -= 100;
            }
        }
        return year;
    }

    function _getParseRegExp(dtf: IDateTimeFormatSettings, format: string) {
        if (!(dtf as any)._parseRegExp) {
            (dtf as any)._parseRegExp = {};
        } else if ((dtf as any)._parseRegExp[format]) {
            return (dtf as any)._parseRegExp[format];
        }

        let expFormat = _expandDateFormat(dtf, format);
        expFormat = expFormat.replace(/([\^\$\.\*\+\?\|\[\]\(\)\{\}])/g, "\\\\$1");
        const regexp: string[] = [];
        const groups = [];
        let index = 0,
            quoteCount = 0;
        const tokenRegExp = /\/|dddd|ddd|dd|d|MMMM|MMM|MM|M|yyyy|yy|y|hh|h|HH|H|mm|m|ss|s|tt|t|fff|ff|f|zzz|zz|z|gg|g/g;
        let match;
        while ((match = tokenRegExp.exec(expFormat)) !== null) {
            const preMatch = expFormat.slice(index, match.index);
            index = tokenRegExp.lastIndex;
            quoteCount += _appendPreOrPostMatch(preMatch, regexp);
            if (quoteCount % 2 === 1) {
                regexp.push(match[0]);
                continue;
            }
            switch (match[0]) {
                case "MMMM":
                case "MMM":
                    regexp.push("([\\d\\D]+)");
                    break;
                case "dddd":
                case "ddd":
                case "gg":
                case "g":
                    regexp.push("(\\D+)");
                    break;
                case "tt":
                case "t":
                    regexp.push("(\\D*)");
                    break;
                case "yyyy":
                    regexp.push("(\\d{4})");
                    break;
                case "fff":
                    regexp.push("(\\d{3})");
                    break;
                case "ff":
                    regexp.push("(\\d{2})");
                    break;
                case "f":
                    regexp.push("(\\d)");
                    break;
                case "dd":
                case "d":
                case "MM":
                case "M":
                case "yy":
                case "y":
                case "HH":
                case "H":
                case "hh":
                case "h":
                case "mm":
                case "m":
                case "ss":
                case "s":
                    regexp.push("(\\d\\d?)");
                    break;
                case "zzz":
                    regexp.push("([+-]?\\d\\d?:\\d{2})");
                    break;
                case "zz":
                case "z":
                    regexp.push("([+-]?\\d\\d?)");
                    break;
                case "/":
                    regexp.push("(\\" + dtf.DateSeparator + ")");
                    break;
                default:
                    throw new Error("Invalid date format pattern.");
            }
            groups.push(match[0]);
        }
        _appendPreOrPostMatch(expFormat.slice(index), regexp);
        regexp.push("$");
        const regexpStr = regexp.join("").replace(/\s+/g, "\\s+");
        const parseRegExp = { regExp: regexpStr, groups: groups };
        (dtf as any)._parseRegExp[format] = parseRegExp;
        return parseRegExp;
    }

    function _indexOf(array: any[], item: any): number {
        if (typeof item === "undefined") {
            return -1;
        }
        for (let i = 0, length = array.length; i < length; i++) {
            if (typeof array[i] !== "undefined" && array[i] === item) {
                return i;
            }
        }
        return -1;
    }
    function _getIndex(value: any, a1: any, a2: any) {
        const upper = _toUpper(value);
        let i = _indexOf(a1, upper);
        if (i === -1) {
            i = _indexOf(a2, upper);
        }
        return i;
    }
    function _getMonthIndex(cultureInfo: ICultureInfo, value: any) {
        if (!(cultureInfo as any)._upperMonths) {
            (cultureInfo as any)._upperMonths = _toUpperArray(cultureInfo.dateTimeFormat.MonthNames);
            (cultureInfo as any)._upperMonthsGenitive = _toUpperArray(cultureInfo.dateTimeFormat.MonthGenitiveNames);
        }
        return _getIndex(value, (cultureInfo as any)._upperMonths, (cultureInfo as any)._upperMonthsGenitive);
    }
    function _getAbbrMonthIndex(cultureInfo: ICultureInfo, value: any) {
        if (!(cultureInfo as any)._upperAbbrMonths) {
            (cultureInfo as any)._upperAbbrMonths = _toUpperArray(cultureInfo.dateTimeFormat.AbbreviatedMonthNames);
            (cultureInfo as any)._upperAbbrMonthsGenitive = _toUpperArray(cultureInfo.dateTimeFormat.AbbreviatedMonthGenitiveNames);
        }
        return _getIndex(value, (cultureInfo as any)._upperAbbrMonths, (cultureInfo as any)._upperAbbrMonthsGenitive);
    }
    function _getDayIndex(cultureInfo: ICultureInfo, value: any) {
        if (!(cultureInfo as any)._upperDays) {
            (cultureInfo as any)._upperDays = _toUpperArray(cultureInfo.dateTimeFormat.DayNames);
        }
        return _indexOf((cultureInfo as any)._upperDays, _toUpper(value));
    }
    function _getAbbrDayIndex(cultureInfo: ICultureInfo, value: any) {
        if (!(cultureInfo as any)._upperAbbrDays) {
            (cultureInfo as any)._upperAbbrDays = _toUpperArray(cultureInfo.dateTimeFormat.AbbreviatedDayNames);
        }
        return _indexOf((cultureInfo as any)._upperAbbrDays, _toUpper(value));
    }
    function _toUpperArray(arr: any) {
        const result = [];
        for (let i = 0, il = arr.length; i < il; i++) {
            result[i] = _toUpper(arr[i]);
        }
        return result;
    }
    function _toUpper(value: any) {
        return value.split("\u00A0").join(" ").toUpperCase();
    }

    function getShortDayMonthPatten(dtf: IDateTimeFormatSettings) { 
        return dtf.ShortDatePattern.replace(/\by{4}\W*|\by{2}\W*|\W*y{4}\b|\W*y{2}\b/i, "");
    }
}

function _stringFormat(useLocale: boolean, format: string, args: any[]): string {
    let result = "";
    for (let i = 0; ;) {
        const open = format.indexOf("{", i);
        const close = format.indexOf("}", i);
        if (open < 0 && close < 0) {
            result += format.slice(i);
            break;
        }

        if (close > 0 && (close < open || open < 0)) {
            if (format.charAt(close + 1) !== "}") {
                throw new Error("The format string contains an unmatched opening or closing brace.");
            }
            result += format.slice(i, close + 1);
            i = close + 2;
            continue;
        }

        result += format.slice(i, open);
        i = open + 1;
        if (format.charAt(i) === "{") {
            result += "{";
            i++;
            continue;
        }

        if (close < 0) {
            throw new Error("The format string contains an unmatched opening or closing brace.");
        }

        const brace = format.substring(i, close);
        const colonIndex = brace.indexOf(":");
        const argNumber = parseInt(colonIndex < 0 ? brace : brace.substring(0, colonIndex), 10);

        if (isNaN(argNumber)) {
            throw new Error("The format string is invalid.");
        }

        const argFormat = colonIndex < 0 ? "" : brace.substring(colonIndex + 1);
        let arg = args[argNumber];
        if (typeof arg === "undefined" || arg === null) {
            arg = "";
        }
        if (arg.toFormattedString) {
            result += arg.toFormattedString(argFormat);
        } else if (arg instanceof Date) {
            result += dateToString(arg, useLocale);
        } else if (arg.format) {
            result += arg.format(argFormat);
        } else {
            result += arg.toString();
        }

        i = close + 1;
    }
    return result;
}
